import { useState } from 'react';
import { Button } from '@repo/ui/Button';
import { Dialog } from '@repo/ui/Dialog';
import { DialogClose } from '@repo/ui/Dialog/DialogClose';
import { DialogContent } from '@repo/ui/Dialog/DialogContent';
import { DialogHeader } from '@repo/ui/Dialog/DialogHeader';
import { DialogRoot } from '@repo/ui/Dialog/DialogRoot';
import { DialogTitle } from '@repo/ui/Dialog/DialogTitle';
import { DialogTrigger } from '@repo/ui/Dialog/DialogTrigger';
import { Flex } from '@repo/ui/Flex';
import { IconButton } from '@repo/ui/IconButton';
import { Text, rawString } from '../../Language/Language';
import { ItemCard } from '../../ItemCard/ItemCard';
import type { OrderItem, Claim } from '../../../graphql/query/getOrder';
import s from './ItemDetailsModal.module.css';

type ItemWithClaim = OrderItem & {
  claims?: Claim[] | null;
};

export type ItemDetailsModalProps = {
  items: ItemWithClaim[];
  itemCountText: string;
  viewDetailsText: string;
};

const INITIAL_DISPLAY_LIMIT = 3;

export const ItemDetailsModal = ({
  items,
  itemCountText,
  viewDetailsText,
}: ItemDetailsModalProps) => {
  const [isExpanded, setIsExpanded] = useState(false);

  if (items.length === 0) {
    return null;
  }

  const itemCount = items.length;
  const displayedItems = isExpanded
    ? items
    : items.slice(0, INITIAL_DISPLAY_LIMIT);
  const hiddenCount = itemCount - INITIAL_DISPLAY_LIMIT;
  const showMoreButton = !isExpanded && hiddenCount > 0;

  const subtitle = rawString({
    langKey:
      itemCount === 1
        ? 'pdx.request_details.items.header.singular'
        : 'pdx.request_details.items.header.plural',
    replacementTokens: { item_count: itemCount.toString() },
  });

  const getReasonDetail = (reason: string) => {
    return rawString({
      langKey: [
        'pdx.confirmation.item_details.none.none.reason',
        'pdx.confirmation.item_details.reason',
      ],
      replacementTokens: { reason },
    });
  };

  return (
    <div className={s.container}>
      <div className={s.header}>
        <span className={s.itemCountText}>{itemCountText}</span>
        <DialogRoot>
          <DialogTrigger asChild={true}>
            <Button variant="plain">{viewDetailsText}</Button>
          </DialogTrigger>
          <Dialog>
            <DialogHeader className={s.modalHeader}>
              <DialogTitle position="center" className={s.modalTitle}>
                <Text langKey="pdx.confirmation.item_details.title" />
              </DialogTitle>
              <DialogClose asChild={true}>
                <IconButton
                  variant="tertiary"
                  size="sm"
                  icon="close"
                  label="Close"
                />
              </DialogClose>
            </DialogHeader>
            <DialogContent className={s.modalContent}>
              <Flex direction="column" justifyContent="space-between">
                <p className={s.subtitle}>{subtitle}</p>
                <div className={s.itemList}>
                  {displayedItems.map((item) => {
                    const claim = item.claims?.[0];
                    const reasonTitle = claim?.reasonId
                      ? rawString({
                          langKey: `pdx.reason.${claim.reasonId}.title`,
                        })
                      : null;

                    const additionalDetails: { name: string; value: string }[] =
                      [
                        {
                          name:
                            rawString({
                              langKey: 'pdx.item_selection.quantity_label',
                            }) ?? 'Quantity',
                          value: '1',
                        },
                        ...(reasonTitle
                          ? [
                              {
                                name:
                                  getReasonDetail(reasonTitle) ??
                                  `Reason: ${reasonTitle}`,
                                value: '',
                              },
                            ]
                          : []),
                      ];

                    return (
                      <ItemCard
                        key={item.id}
                        item={item}
                        imageSize="sm"
                        additionalDetails={additionalDetails}
                        containerClassName={s.modalItemCardWrapper}
                      />
                    );
                  })}
                </div>
                {showMoreButton && (
                  <button
                    type="button"
                    className={s.showMoreButton}
                    onClick={() => setIsExpanded(true)}
                  >
                    <Text
                      langKey="pdx.confirmation.item_details.show_more"
                      replacementTokens={{ count: hiddenCount.toString() }}
                    />
                  </button>
                )}
                <Flex justifyContent="center" className={s.modalFooter}>
                  <DialogClose asChild={true}>
                    <Button
                      isFullWidth
                      variant="secondary"
                      size="sm"
                      className={s.closeButton}
                    >
                      <Text langKey="pdx.global.modal.close" />
                    </Button>
                  </DialogClose>
                </Flex>
              </Flex>
            </DialogContent>
          </Dialog>
        </DialogRoot>
      </div>
      <div className={s.thumbnailRow}>
        {items.slice(0, 4).map((item) => (
          <div key={item.id} className={s.thumbnail}>
            {item.image?.url ? (
              <img
                src={item.image.url}
                alt={item.name}
                className={s.thumbnailImage}
                loading="lazy"
              />
            ) : (
              <div className={s.thumbnailPlaceholder} aria-hidden="true" />
            )}
          </div>
        ))}
        {items.length > 4 && (
          <div className={s.moreItemsIndicator}>+{items.length - 4}</div>
        )}
      </div>
    </div>
  );
};
